import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/providers/auth-provider";
import { getPlayerQueueStatus } from "@/services/duel/get-player-queue-status";

export type DuelQueueStatus = "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";

/**
 * Hook to manage duel queue status polling.
 * Polls every 8 seconds when not idle and enabled.
 */
export function useDuelQueue(
  enabled: boolean = true,
  initialData?: { status: DuelQueueStatus; duelId: string | null },
) {
  const { getAccessToken } = useAuth();
  const [queueStatus, setQueueStatus] = useState<DuelQueueStatus>(
    initialData?.status ?? "IDLE",
  );
  const [currentDuelId, setCurrentDuelId] = useState<string | null>(
    initialData?.duelId ?? null,
  );
  const queueStatusRef = useRef(queueStatus);

  useEffect(() => {
    queueStatusRef.current = queueStatus;
  }, [queueStatus]);

  const fetchQueueStatus = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      setQueueStatus("IDLE");
      return;
    }

    try {
      const response = await getPlayerQueueStatus(accessToken);
      if (response.ok && response.data) {
        setQueueStatus(response.data.status);
        setCurrentDuelId(response.data.queue_entry?.duel_id ?? null);
      } else {
        console.error("Failed to fetch queue status:", response.message);
        setQueueStatus("IDLE");
      }
    } catch (error) {
      console.error("Failed to fetch queue status:", error);
      setQueueStatus("IDLE");
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (!enabled) return;

    fetchQueueStatus();

    const interval = setInterval(() => {
      if (queueStatusRef.current === "IDLE") return;
      fetchQueueStatus();
    }, 8000);

    return () => clearInterval(interval);
  }, [enabled, fetchQueueStatus]);

  return { queueStatus, currentDuelId, refetch: fetchQueueStatus };
}

